import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api, { formatMoney, formatMoney2, formatNumber, formatApiErrorDetail } from "../lib/api";
import { useHistoryBack } from "../lib/useHistoryBack";
import { VolverButton } from "./VolverButton";

const fecha = (v) => (v ? new Date(v).toLocaleDateString("es-ES") : "—");
const diasDesde = (v) => (v ? Math.floor((Date.now() - new Date(v).getTime()) / 86400000) : null);

function Dato({ label, value, sub, tone, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={"font-display text-xl font-extrabold mt-1 " + (tone || "")}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Contenido({ d }) {
  const p = d.producto;
  const margen = p.pvp && p.pmc ? (100 * (p.pvp - p.pmc)) / p.pvp : null;
  const de = diasDesde(p.u_entrada), ds = diasDesde(p.u_salida);
  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Dato label="Stock que queda" value={`${formatNumber(p.stock)} uds`} sub={`${formatMoney2(p.valor_pvp)} a PVP · ${formatMoney2(p.valor_pmc)} a coste`} testid="ficha-stock" />
        <Dato label="Ventas del estudio" value={formatMoney(p.tot_venta)} sub={`${formatNumber(p.uds_vendidas)} uds${d.period_days ? ` en ${d.period_days} días` : ""}`} testid="ficha-ventas" />
        <Dato label="Última entrada" value={fecha(p.u_entrada)} sub={de != null ? `hace ${formatNumber(de)} días` : null}
              tone={de != null && de > 365 ? "text-red-600" : ""} testid="ficha-ultima-entrada" />
        <Dato label="Última venta" value={fecha(p.u_salida)} sub={ds != null ? `hace ${formatNumber(ds)} días` : null} testid="ficha-ultima-venta" />
        <Dato label="PVP" value={formatMoney2(p.pvp)} sub={`Coste ${formatMoney2(p.pmc)}${margen != null ? ` · margen ${margen.toFixed(1).replace(".", ",")} %` : ""}`} testid="ficha-pvp" />
        <Dato label="Clase ABCD" value={p.abcd || "—"} sub={p.d_type === "D_MUERTO" ? "Stock D real (sin entradas en más de 1 año)" : null} testid="ficha-abcd" />
        <Dato label="Caducidad" value={fecha(p.caducidad)} testid="ficha-caducidad" />
        <Dato label="Mín / Máx" value={p.stock_min != null || p.stock_max != null ? `${p.stock_min ?? "—"} / ${p.stock_max ?? "—"}` : "—"} testid="ficha-minmax" />
      </div>
      <div className="card p-6" data-testid="ficha-mensual">
        <h3 className="font-display text-lg font-bold">Ventas mes a mes</h3>
        <p className="text-xs text-slate-500 mb-3">Últimos 12 meses de ventas subidos.{d.mensual.some((m) => m.parcial_dias) ? " Algunos meses están subidos a medias." : ""}</p>
        {d.mensual.length === 0 ? <p className="text-sm text-slate-500">Aún no hay ventas mensuales subidas.</p> : (
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={d.mensual}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} tickFormatter={(l) => l.slice(0, 3) + " " + l.slice(-2)} />
              <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickFormatter={(v) => formatMoney(v)} width={70} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v, n) => (n === "Unidades" ? formatNumber(v) : formatMoney2(v))}
                       labelFormatter={(l, pl) => l + (pl?.[0]?.payload?.parcial_dias ? ` (${pl[0].payload.parcial_dias} días)` : pl?.[0]?.payload?.con_datos === false ? " (sin datos)" : "")} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="l" dataKey="ventas" name="Ventas" fill="#0f172a" radius={[4, 4, 0, 0]} />
              <Line yAxisId="r" dataKey="uds" name="Unidades" stroke="#10b981" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      {d.vendedores.length > 0 && (
        <div className="card p-6" data-testid="ficha-vendedores">
          <h3 className="font-display text-lg font-bold mb-3">Quién lo vende más</h3>
          <table className="dense">
            <thead><tr><th>#</th><th>Empleado</th><th className="num">Unidades</th><th className="num">Ventas</th></tr></thead>
            <tbody>{d.vendedores.map((v, i) => (
              <tr key={v.vendedor}><td className="font-mono text-xs text-slate-500">{i + 1}</td><td className="font-semibold">{v.vendedor}</td>
                <td className="num">{formatNumber(v.uds)}</td><td className="num">{formatMoney2(v.ventas)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function FichaProducto({ cn, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const nav = useHistoryBack(!!cn, onClose);
  useEffect(() => {
    if (!cn) return;
    setD(null); setErr("");
    api.get("/productos/ficha", { params: { cn } }).then((r) => setD(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo cargar la ficha."));
  }, [cn]);
  if (!cn) return null;
  const p = d?.producto;
  return (
    <div className="fixed inset-0 z-[60] bg-slate-50 flex flex-col fade-in" data-testid="ficha-producto">
      <div className="px-6 py-3 bg-white border-b border-slate-200 flex items-start gap-4">
        <VolverButton onClick={nav.back} testid="ficha-volver" className="mt-1" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Ficha de producto · CN <span className="font-mono">{cn}</span></div>
          <h2 className="font-display text-xl font-extrabold tracking-tight" data-testid="ficha-titulo">{p?.descripcion || "Cargando…"}</h2>
          {p && <p className="text-sm text-slate-600 mt-1">{p.laboratorio} · {p.familia}{d.study_name ? ` · Estudio ${d.study_name}` : ""}</p>}
        </div>
        <button onClick={nav.close} className="p-2 rounded hover:bg-slate-100 text-slate-500" data-testid="ficha-cerrar"><X size={20} /></button>
      </div>
      <div className="flex-1 overflow-auto">
        {err ? <div className="p-6 text-slate-600" data-testid="ficha-error">{err}</div>
          : !d ? <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Cargando ficha…</div>
            : <Contenido d={d} />}
      </div>
    </div>
  );
}
